// src/components/resources/tools/generators/FakeChat/FakeChatGenerator.tsx

'use client'

import { useRef } from 'react'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'
import { DeferredFakeChatStateProvider, FakeChatProvider } from './FakeChatContext'
import { FakeChatControls } from './FakeChatControls'
import { FakeChatPreview } from './FakeChatPreview'
import type { ChatPlatform, ConversationMetadata } from './FakeChat.types'

/**
 * Fallback shown if the preview subtree throws during render. Production logs
 * captured a recurring `RangeError: Maximum call stack size exceeded` on iOS
 * (WebKit's call-stack limit is far smaller than V8's) that escaped to
 * `window.onerror` and blanked the page. Scoping a boundary to the preview keeps
 * the Controls column (a sibling, outside this boundary) interactive, and routes
 * the fault through the shared `ErrorBoundary` → `handleClientError` drain with a
 * React component stack — far more actionable than the bare minified
 * `window.onerror` frames.
 *
 * The message deliberately does NOT promise that editing a field recovers the
 * preview: the boundary latches `hasError` until it unmounts (it has no
 * reset-on-prop-change), and the RangeError is deterministic anyway, so a
 * re-render would just re-throw. A full reload is the honest recovery path.
 */
function FakeChatPreviewFallback(): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200"
    >
      <p className="font-semibold">The preview hit a snag.</p>
      <p className="mt-1">
        Your settings in the controls are safe. Refresh the page to reload the
        preview.
      </p>
    </div>
  )
}

/**
 * Props for the Fake Chat Generator.
 *
 * Used by landing-page variants registered in `landingVariants.ts`
 * (iMessage, Slack, group-chat) to boot the generator pre-configured
 * for a specific platform. The provider applies the preset
 * synchronously inside its reducer initializer so the first render is
 * already correct.
 */
export interface FakeChatGeneratorProps {
  initialPlatform?: ChatPlatform
  initialChannelType?: ConversationMetadata['channelType']
  /**
   * Resource slug used to attribute export analytics. Defaults to the
   * umbrella `'fake-chat-generator'`; landing-page variants (iMessage,
   * Slack, group-chat) pass their own slug so per-variant export
   * conversions aren't folded into the umbrella's counts.
   */
  trackingSlug?: string
}

/**
 * High-level layout component that wires the Fake Chat provider, controls, and preview together.
 *
 * The `previewRef` is owned here (it wraps the rendered preview) and passed
 * into `FakeChatControls` so the Download button can capture the styled
 * mockup with html2canvas — mirroring the SocialMedia / EmailThread export
 * shape. The surrounding layout is intentionally unchanged from the
 * pre-export shell.
 */
export function FakeChatGenerator({ initialPlatform, initialChannelType, trackingSlug }: FakeChatGeneratorProps = {}): JSX.Element {
  const previewRef = useRef<HTMLDivElement>(null)
  // Set to `true` by the deferred boundary while the preview trails live state;
  // FakeChatControls reads it before an export so Download can't capture a
  // still-pending deferred frame.
  const previewStaleRef = useRef(false)

  return (
    <FakeChatProvider initialPlatform={initialPlatform} initialChannelType={initialChannelType}>
      {/*
        Top padding and min-height are desktop-only: on mobile the umbrella
        layout already supplies the spacing above the tool, and `min-h-screen`
        exists solely to give the `lg:` sticky controls column room to scroll —
        on mobile both only produced dead space above and below the chat.
      */}
      <div className="flex w-full max-w-7xl flex-col gap-8 px-0 pb-12 lg:min-h-screen lg:flex-row lg:items-start lg:gap-12 lg:pt-12">
        <div className="w-full lg:sticky lg:top-28 lg:w-[28rem] lg:shrink-0 xl:w-[32rem]">
          {/* Keeping the controls column sticky means the new tabbed navigation stays visible while the preview scrolls. */}
          <FakeChatControls previewRef={previewRef} previewStaleRef={previewStaleRef} trackingSlug={trackingSlug} />
        </div>
        <div className="min-w-0 flex-1" ref={previewRef}>
          {/*
            Render the heavy preview from a deferred state snapshot so
            keystrokes in the Controls column stay responsive on mobile
            (cuts the long interaction task behind the ~392ms mobile INP).
            The preview has no state-bound inputs and export waits for the
            deferred snapshot to converge before reading the DOM, so the
            resting output and export are unchanged.
          */}
          <ErrorBoundary name="fake-chat-preview" fallback={<FakeChatPreviewFallback />}>
            <DeferredFakeChatStateProvider staleRef={previewStaleRef}>
              <FakeChatPreview />
            </DeferredFakeChatStateProvider>
          </ErrorBoundary>
        </div>
      </div>
    </FakeChatProvider>
  )
}
